import angular from "angular";

angular
    .module('ui-sentinel.session')
    .factory('SentinelUiSession', SentinelUiSession);

export class SupersededAuthResponseError extends Error {
    constructor() {
        super('Superseded web-auth response');
        this.name = 'SupersededAuthResponseError';
    }
}

/**
 * Raised instead of calling /auth/web/refresh once the session is known to be gone —
 * after an explicit logout, or after a refresh already failed fatally. Callers treat it
 * as "no session" rather than as an auth failure, so it never expires or redirects.
 */
export class NoRefreshSessionError extends Error {
    constructor() {
        super('No web-auth refresh session');
        this.name = 'NoRefreshSessionError';
    }
}

SentinelUiSession.$inject = ['$rootScope', '$q', '$window', 'ApiToken', 'LoginsApiService', 'AccountApiService', 'localStorageService', 'USER_ROLES', 'TrackingConfigService', 'SentinelAuthenticationService', 'SENTINEL_API_HOST_CONSTANTS'];

export function SentinelUiSession($rootScope, $q, $window, ApiToken, LoginsApiService, AccountApiService, localStorageService, USER_ROLES, TrackingConfigService, SentinelAuthenticationService, HOST) {

    const refreshLeadSeconds = 120;
    const refreshJitterSeconds = 60;
    const refreshRetryMilliseconds = 30000;
    // ~5 minutes of 30s retries. Long enough to ride out a sleep/wake network gap, short
    // enough that a refresh which will never succeed still ends the session.
    const maxConsecutiveTransientFailures = 10;
    let refreshTimeoutId = undefined;
    let refreshPromise = undefined;
    let refreshDeferredWhileHidden = false;
    let refreshUnavailable = false;
    let consecutiveTransientFailures = 0;
    let sessionVersion = 0;

    const useAutoLogout = true;
    let autoLogoutIntervalId = undefined;

    const service = {
        user: null,
        focus: null,
        mode: 'account',
        login,
        logout,
        ensureSession,
        refreshAccessToken,
        handleRefreshFailure,
        isSupersededResponse,
        expireSession,
        setFocus,
        clearFocus,
        set,
        clear,
        isValid,
        isExpired,
        isEulaAgreement,
        store,
        load,
        isReloaded: false,
        showShipmentModule,
        showWarehouseModule,
        showCalibrationModule,
        showPivotModule
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return service;

    ///////////////////////////

    /**
     * On by default on QA and local builds, off in production (WEB_AUTH_LOG, substituted at
     * build time). Read fresh each call so it can be flipped from devtools mid-session.
     *
     * Deliberately raw localStorage rather than localStorageService: that service prefixes
     * its keys and clearAll() wipes them on logout, and logout is exactly when the trace
     * matters most. An unprefixed key survives.
     */
    function isAuthLogEnabled() {
        try {
            const override = $window.localStorage.getItem('webAuthLog');
            if (override !== null && override !== undefined) {
                return override === 'true';
            }
        } catch (error) {
            // Storage unavailable (private mode, blocked third-party storage) - fall back
            // to the build-time default.
        }
        return HOST && HOST.WEB_AUTH_LOG === 'true';
    }

    /**
     * Breadcrumbs for diagnosing a session that ended unexpectedly. Never logs a token value.
     * Volume is roughly one line per refresh cycle.
     */
    function authLog(event, detail) {
        if (!isAuthLogEnabled()) {
            return;
        }
        const line = '[web-auth] ' + new Date().toISOString() + ' ' + event;
        if (detail === undefined) {
            console.log(line);
        } else {
            console.log(line, detail);
        }
    }

    function describeRefreshError(error) {
        return {
            // xhrStatus separates a real HTTP answer from a connection that never landed:
            // AngularJS reports 'error' | 'timeout' | 'abort' with status -1.
            status: error ? error.status : undefined,
            xhrStatus: error ? error.xhrStatus : undefined,
            transient: isTransientRefreshFailure(error),
            consecutiveTransientFailures: consecutiveTransientFailures
        };
    }

    function expireSession() {
        authLog('session-expired');
        refreshUnavailable = true;
        clearLocalSession();
        $window.location.href = '/#!/login';
    }

    function refreshClear() {
        if (refreshTimeoutId !== undefined) {
            clearTimeout(refreshTimeoutId);
            refreshTimeoutId = undefined;
        }
    }

    function autoLogoutClear() {
        if (autoLogoutIntervalId) {
            clearInterval(autoLogoutIntervalId);
            autoLogoutIntervalId = undefined;
        }
    }

    function scheduleRefresh(expiresInSeconds) {
        refreshClear();
        refreshDeferredWhileHidden = false;
        const jitter = Math.floor(Math.random() * (refreshJitterSeconds + 1));
        const delayMilliseconds = Math.max(
            1000,
            (expiresInSeconds - refreshLeadSeconds - jitter) * 1000
        );
        authLog('refresh-scheduled', {
            inSeconds: Math.round(delayMilliseconds / 1000),
            tokenExpiresInSeconds: Math.round(expiresInSeconds)
        });
        refreshTimeoutId = setTimeout(refreshWhenDue, delayMilliseconds);
    }

    function scheduleRefreshFromStorage() {
        const expirationTime = new Date(localStorageService.get('tokenExpirationTime'));
        const secondsRemaining = (expirationTime.getTime() - new Date().getTime()) / 1000;
        if (Number.isFinite(secondsRemaining) && secondsRemaining > 0) {
            scheduleRefresh(secondsRemaining);
        }
    }

    function scheduleRefreshRetry() {
        refreshClear();
        authLog('refresh-retry-scheduled', {
            inSeconds: refreshRetryMilliseconds / 1000,
            consecutiveTransientFailures: consecutiveTransientFailures
        });
        refreshTimeoutId = setTimeout(refreshWhenDue, refreshRetryMilliseconds);
    }

    /**
     * Counts a transient failure and reports whether another retry is still allowed.
     * Returns false once the run of failures is long enough to call the session dead.
     */
    function noteTransientFailure() {
        consecutiveTransientFailures += 1;
        if (consecutiveTransientFailures > maxConsecutiveTransientFailures) {
            authLog('refresh-transient-limit-reached', {
                attempts: consecutiveTransientFailures,
                limit: maxConsecutiveTransientFailures
            });
            return false;
        }
        return true;
    }

    function refreshWhenDue() {
        if (document.hidden) {
            refreshDeferredWhileHidden = true;
            authLog('refresh-deferred-hidden');
            scheduleRefreshRetry();
            return;
        }
        refreshDeferredWhileHidden = false;
        authLog('refresh-due');
        refreshAccessToken().catch(handleRefreshFailure);
    }

    function isRefreshOverdue() {
        // A cleared session must not read as overdue. localStorageService returns null for a
        // missing key and new Date(null) is the epoch — finite and long past — so without
        // this guard every visibilitychange fired a refresh that could only ever 401.
        const storedExpiration = localStorageService.get('tokenExpirationTime');
        if (!storedExpiration) {
            return false;
        }
        const secondsRemaining = (new Date(storedExpiration).getTime() - new Date().getTime()) / 1000;
        return Number.isFinite(secondsRemaining) && secondsRemaining <= refreshLeadSeconds;
    }

    function onVisibilityChange() {
        if (document.hidden) {
            return;
        }
        if (!refreshDeferredWhileHidden && !isRefreshOverdue()) {
            return;
        }
        authLog('visibility-visible-refreshing', {
            deferredWhileHidden: refreshDeferredWhileHidden,
            overdue: isRefreshOverdue()
        });
        refreshDeferredWhileHidden = false;
        refreshClear();
        refreshAccessToken().catch(handleRefreshFailure);
    }

    function autoLogout(seconds) {
        if (!useAutoLogout) {
            return;
        }
        autoLogoutClear();
        const logoutMs = seconds * 1000;
        const logoutTime = new Date().getTime() + logoutMs;
        const intervalSeconds = Math.min(60, seconds);
        autoLogoutIntervalId = setInterval(() => {
            const secondsLeft = (logoutTime - new Date().getTime()) / 1000;
            if (secondsLeft <= 0) {
                // Do not hard-expire on the local deadline alone: a hidden or
                // throttled tab may simply have an overdue refresh. Try to
                // refresh first; handleRefreshFailure expires the session only
                // when the refresh fails with an auth-fatal (non-transient) error.
                autoLogoutClear();
                authLog('auto-logout-deadline-refreshing');
                refreshAccessToken().catch(handleRefreshFailure);
                return;
            }
            if (secondsLeft < 60) {
                autoLogout(secondsLeft);
            }
        }, intervalSeconds * 1000);
    }

    function login(username, password) {
        clearLocalSession();
        const loginVersion = sessionVersion;
        return SentinelAuthenticationService.login(username, password)
            .then(function (response) {
                storeTokenResponse(response.data, loginVersion);
                return populateSession(response.data, loginVersion);
            })
            .catch(function (error) {
                if (loginVersion !== sessionVersion) {
                    return $q.reject(new SupersededAuthResponseError());
                }
                return $q.reject(error);
            });
    }

    function populateSession(tokenResponse, responseVersion) {
        return LoginsApiService.getCurrent().$promise.then(function (login) {
            if (responseVersion !== sessionVersion) {
                return $q.reject(new SupersededAuthResponseError());
            }

            if (login.clientRedirect && typeof login.clientRedirect === 'string' && login.clientRedirect.length > 0) {
                return logout().then(function () {
                    $rootScope.loading = false;
                    $rootScope.$broadcast('clientRedirect', login.clientRedirect);
                    return {redirected: true};
                });
            }

            return AccountApiService.getAccount().$promise.then(function (account) {
                if (responseVersion !== sessionVersion) {
                    return $q.reject(new SupersededAuthResponseError());
                }

                let isExpired = false;
                if (login.dateLockoutExpiresTimestamp && !login.role.startsWith("api")) {
                    const timeDiff = (login.dateLockoutExpiresTimestamp * 1000) - new Date().getTime();
                    if (timeDiff < 0)
                        isExpired = true;
                }
                const eulaAgreement = login.eulaAgreement;
                service.user = {
                    id: login.id,
                    name: login.userName,
                    role: login.role,
                    isExpired: isExpired,
                    isAnAdmin: login.role === 'api-admin' || login.role === 'api-login' || login.role === USER_ROLES.systemAdmin || login.role === USER_ROLES.accountAdmin || login.role === USER_ROLES.supportAdmin,
                    isSystemAdmin: login.role === 'api-admin' || login.role === USER_ROLES.systemAdmin,
                    isSupportAdmin: login.role === USER_ROLES.supportAdmin,
                    isSupportObserver: login.role === USER_ROLES.supportObserver,
                    isAccountAdmin: login.role === 'api-login' || login.role === USER_ROLES.accountAdmin,
                    isAccountEditor: login.role === USER_ROLES.accountEditor,
                    isAccountObserver: login.role === USER_ROLES.accountObserver,
                    account: account,
                    accountId: account.id,
                    accountName: account.name,
                    accountAccessLevel: account.accessLevel,
                    accountIsTopParent: !account.parentId,
                    accountIsSubAccount: !!account.parentId,
                    isEulaAgreement: !!eulaAgreement,
                    shipmentTrackingPrefix: account.shipmentTrackingPrefix,
                    trackingUrl: account.trackingUrl,
                    isOrsTopLevel: account.isOrsTopLevel,
                    isSuntronic: account.isSuntronic,
                    isAiTracking: account.isAiTracking,
                };

                setFocus(account);
                store();
                autoLogout(Number(tokenResponse.expires_in));
                return {redirected: false};
            });
        });
    }

    function set(user, focus) {
        service.user = user;
        if (user) {
            service.user = angular.extend(user, {
                isAnAdmin: user.role === 'api-admin' || user.role === 'api-login',
                isSystemAdmin: user.role === USER_ROLES.systemAdmin,
                isSupportAdmin: user.role === USER_ROLES.supportAdmin,
                isSupportObserver: user.role === USER_ROLES.supportObserver,
                isAccountAdmin: user.role === USER_ROLES.accountAdmin,
                isAccountEditor: user.role === USER_ROLES.accountEditor,
                isAccountObserver: user.role === USER_ROLES.accountObserver,
            });
        }
        service.focus = focus;
        loadTrackingConfig();
        localStorageService.set('user', service.user);
        localStorageService.set('focus', service.focus);
    }

    function setFocus(account) {
        service.focus = {
            id: account ? account.id : service.user.accountId,
            name: account ? account.name : service.user.accountName,
            accessLevel: account ? account.accessLevel : service.user.accountAccessLevel,
            isTopParent: account ? !account.parentId : service.user.accountIsTopParent,
            isSubAccount: account ? !!account.parentId : service.user.accountIsSubAccount,
            shipmentTrackingPrefix: account ? account.shipmentTrackingPrefix : service.user.shipmentTrackingPrefix,
            trackingUrl: account ? account.trackingUrl : service.user.trackingUrl,
            isFocused: account && account.id !== service.user.accountId,
            isAiTracking: account && account.isAiTracking,
        };
        loadTrackingConfig();
        localStorageService.set('focus', service.focus);
    }

    function clearFocus() {
        const account = service.user ? service.user.account : null;
        setFocus(account);
    }

    function store() {
        const authToken = ApiToken.get();
        localStorageService.set('token', authToken.token);
        localStorageService.set('tokenType', authToken.type);
        localStorageService.set('tokenExpirationTime', authToken.expiresAt);
        localStorageService.set('user', service.user);
        localStorageService.set('focus', service.focus);
    }

    function load() {
        const tokenExpirationTime = new Date(localStorageService.get('tokenExpirationTime'));
        ApiToken.set(
            localStorageService.get('token'),
            localStorageService.get('tokenType'),
            tokenExpirationTime);
        service.user = localStorageService.get('user');
        service.focus = localStorageService.get('focus');
        service.isReloaded = true;
        if (ApiToken.isValid() && service.user && service.focus) {
            const seconds = (tokenExpirationTime - new Date()) / 1000;
            scheduleRefresh(seconds);
            autoLogout(seconds);
        }
    }

    function clear() {
        clearLocalSession();
    }

    function clearLocalSession() {
        sessionVersion++;
        refreshPromise = undefined;
        refreshDeferredWhileHidden = false;
        consecutiveTransientFailures = 0;
        refreshClear();
        autoLogoutClear();
        ApiToken.clear();
        service.user = null;
        service.focus = null;
        service.isReloaded = false;
        localStorageService.clearAll();
    }

    function storeTokenResponse(tokenResponse, responseVersion) {
        if (responseVersion !== sessionVersion) {
            throw new SupersededAuthResponseError();
        }
        const expiresIn = Number(tokenResponse.expires_in);
        if (!tokenResponse.access_token || !tokenResponse.token_type || !Number.isFinite(expiresIn) || expiresIn <= 0) {
            throw new Error('Invalid web-auth token response');
        }
        refreshUnavailable = false;
        consecutiveTransientFailures = 0;
        ApiToken.set(
            tokenResponse.access_token,
            tokenResponse.token_type,
            ApiToken.getExpirationDate(expiresIn)
        );
        authLog('token-stored', {
            expiresInSeconds: expiresIn,
            expiresAt: new Date(ApiToken.get().expiresAt).toISOString()
        });
        localStorageService.set('token', tokenResponse.access_token);
        localStorageService.set('tokenType', tokenResponse.token_type);
        localStorageService.set('tokenExpirationTime', ApiToken.get().expiresAt);
        scheduleRefresh(expiresIn);
        autoLogout(expiresIn);
    }

    function refreshAccessToken() {
        if (refreshUnavailable) {
            return $q.reject(new NoRefreshSessionError());
        }

        if (refreshPromise) {
            return refreshPromise;
        }

        const refreshVersion = sessionVersion;
        authLog('refresh-start');
        let sharedPromise;
        const request = SentinelAuthenticationService.refresh()
            .then(function (response) {
                storeTokenResponse(response.data, refreshVersion);
                return response.data;
            })
            .catch(function (error) {
                if (refreshVersion !== sessionVersion) {
                    authLog('refresh-superseded');
                    return $q.reject(new SupersededAuthResponseError());
                }
                authLog('refresh-failed', describeRefreshError(error));
                return $q.reject(error);
            });

        sharedPromise = request.finally(function () {
            if (refreshPromise === sharedPromise) {
                refreshPromise = undefined;
            }
        });
        refreshPromise = sharedPromise;
        return refreshPromise;
    }

    function ensureSession() {
        if (isValid()) {
            scheduleRefreshFromStorage();
            return $q.resolve(true);
        }

        return refreshAccessToken()
            .then(function (tokenResponse) {
                if (service.user && service.focus) {
                    return true;
                }
                const responseVersion = sessionVersion;
                return populateSession(tokenResponse, responseVersion)
                    .then(function (result) {
                        return !result.redirected;
                    });
            })
            .catch(function (error) {
                if (error instanceof SupersededAuthResponseError) {
                    return isValid();
                }
                if (error instanceof NoRefreshSessionError) {
                    return false;
                }
                if (isTransientRefreshFailure(error) && noteTransientFailure()) {
                    scheduleRefreshRetry();
                    return false;
                }
                refreshUnavailable = true;
                clearLocalSession();
                return false;
            });
    }

    function logout() {
        authLog('logout');
        refreshUnavailable = true;
        clearLocalSession();
        return SentinelAuthenticationService.logout()
            .then(
                function () {},
                function () {}
            );
    }

    function handleRefreshFailure(error) {
        if (error instanceof SupersededAuthResponseError || error instanceof NoRefreshSessionError) {
            return;
        }
        if (isTransientRefreshFailure(error) && noteTransientFailure()) {
            scheduleRefreshRetry();
            return;
        }
        expireSession();
    }

    function isSupersededResponse(error) {
        return error instanceof SupersededAuthResponseError;
    }

    function isTransientRefreshFailure(error) {
        // AngularJS reports a request that never got an HTTP answer - connection failure,
        // CORS rejection, timeout, abort - as status -1, never 0 (see angular.js
        // completeRequest/resolvePromise). Waking from sleep before wifi reassociates lands
        // here, and it must retry rather than end the session.
        return !!error && (error.status <= 0 || error.status === 429 || error.status >= 500);
    }

    function isValid() {
        if (!service.user || !service.focus) {
            load();
        }

        return ApiToken.isValid() && service.user && service.focus;
    }

    function isExpired() {
        if (!service.user || !service.focus) {
            load();
        }
        return ApiToken.isValid() && service.user && service.user.isExpired;
    }

    function isEulaAgreement() {
        if (!service.user || !service.focus) {
            load();
        }
        return ApiToken.isValid() && service.user && service.user.isEulaAgreement;
    }

    function showShipmentModule() {
        console.log("showShipmentModule");
        if (service.user) {
            if (service.user.isSystemAdmin && (service.focus.id === service.user.accountId)) {
                return true;
            } else {
                return _.find(service.focus.accessLevel, function (role) {
                    return role === "Shipment";
                });
            }
        }

        return false;
    }

    function showWarehouseModule() {
        if (service.user) {
            if (service.user.isSystemAdmin && (service.focus.id === service.user.accountId))
                return true;
            else {
                return _.find(service.focus.accessLevel, function (role) {
                    return role === "Warehouse";
                });
            }
        }

        return false;
    }

    function showCalibrationModule() {
        if (service.user) {
            if (service.user.isSystemAdmin && (service.focus.id === service.user.accountId))
                return true;
            else {
                return _.find(service.focus.accessLevel, function (role) {
                    return role === "Calibration";
                });
            }
        }

        return false;
    }

    function showPivotModule() {
        if (service.user) {
            if (service.user.isSystemAdmin && (service.focus.id === service.user.accountId))
                return true;
            else {
                return _.find(service.focus.accessLevel, function (role) {
                    return role === "Pivot";
                });
            }
        }

        return false;
    }

    function loadTrackingConfig() {

        const promise = TrackingConfigService.getConfig(service.focus).$promise;
        promise.then(
            function (result) {
                if (result) {
                    service.focus.trackingConfig = { "referencePrefix": result.shipmentReferencePrefix };
                    store();
                } else
                    service.focus.trackingConfig = null;
            },
            function (error) {

            }
        );
    }
}
